import { fileAPI } from "services";
import { useState } from "react";
import ModalImage from "react-modal-image";

const ProfilePhoto = ({ url, loading, updateField, isDisabled, onRemove }) => {
  const [values, setValues] = useState({ errormsg: "", preview: url || null });
  const [progress, setProgress] = useState(0); // Add progress to state

  const DEFAULT_IMAGE_URL = "https://placehold.co/150x150";

  const handleProfileSelect = async (e) => {
    e.preventDefault();
    const newfile = e.target.files[0];

    // return if no file selected
    if (!newfile) return;

    // Check if the selected file is an image by inspecting the MIME type
    if (!newfile.type.startsWith("image/")) {
      failure("Only image files are allowed.");
      updateField("photo", { url: null, file: null, loading: false });
      setProgress(0);
      return; // Prevent further processing
    }

    try {
      if (newfile.size <= 2097152 && e.target.files.length <= 1) {
        updateField("photo", { file: newfile, loading: true });
        // let currentProgress = 0; // Local variable for progress
        // const progressInterval = setInterval(() => {
        //   if (currentProgress < 100) {
        //     currentProgress += 20; // Increment progress (adjust step if needed)
        //     if (currentProgress >= 100) {
        //       clearInterval(progressInterval); // Stop the interval when progress reaches 100
        //     }
        //     setProgress(currentProgress); // Update state with progress
        //   }
        // }, 500); // Update progress every second
        const objectUrl = URL.createObjectURL(newfile);
        success(objectUrl, newfile);
        updateField("photo", { url: objectUrl, file: newfile, loading: true });
        const result = await fileAPI.uploadImage(newfile, setProgress);
        updateField("photo", { url: result._url, file: newfile, loading: false });
        setProgress(0);
      } else if (e.target.files.length > 1) {
        failure("Only one file allowed");
        updateField("photo", { url: null, file: null, loading: false });
      } else {
        failure("File exceeds the size limit of 2MB.");
        updateField("photo", { url: null, file: null, loading: false });
      }
    } catch (error) {
      updateField("photo", { url: null, file: null, loading: false });
      setProgress(0);
      if (error.message === "Filename contains invalid characters.") {
        failure(error.message);
      } else {
        failure("Try again, Image upload failed");
      }
    }
  };

  const success = (objectUrl, file) => {
    setValues({
      errormsg: "",
      preview: objectUrl,
    });
  };

  const failure = (message) => {
    updateField("photo", { url: null, file: null });

    setProgress(0);

    setValues((prevValues) => ({
      ...prevValues,
      errormsg: message,
      preview: prevValues.preview,
    }));

    setTimeout(() => {
      setValues((prevValues) => ({
        ...prevValues,
        errormsg: "",
      }));
    }, 5000);
  };

  const removeImage = async () => {
    console.log("removeImage");

    updateField("photo", { url: null, file: null });
    setValues({ errormsg: "", preview: null });
    onRemove?.();
  };

  // UI component to show camera icon to select image
  const renderCameraIcon = () => (
    <label htmlFor="photo" className="cursor-pointer mb-0">
      <div
        className="btn-app rounded-circle shadow d-flex align-items-center justify-content-center position-absolute"
        style={{ width: "34px", height: "34px", border: "2.5px solid #fff", bottom: "6px", right: "6px", zIndex: 2 }}
      >
        <i className="bi bi-camera-fill text-white" style={{ fontSize: "14px" }}></i>
      </div>
    </label>
  );

  // UI component to show edit and remove/change image options when an image is set
  const renderEditOptions = () => (
    <div className="dropdown position-absolute" style={{ bottom: "6px", right: "6px", zIndex: 2 }}>
      <button
        className="btn-app rounded-circle shadow d-flex align-items-center justify-content-center p-0"
        type="button"
        data-bs-toggle="dropdown"
        style={{ width: "34px", height: "34px", border: "2.5px solid #fff" }}
      >
        <i className="bi bi-pencil-fill text-white" style={{ fontSize: "13px" }}></i>
      </button>

      <ul className="dropdown-menu dropdown-menu-end shadow border-0 rounded-3 p-1">
        <li>
          <label
            className="dropdown-item d-flex align-items-center gap-2 rounded-2 mb-0 py-2 cursor-pointer"
            htmlFor="photo"
          >
            <i className="bi bi-arrow-repeat"></i>
            Change Photo
          </label>
        </li>
        <li><hr className="dropdown-divider my-1" /></li>
        <li>
          <button
            className="dropdown-item d-flex align-items-center gap-2 rounded-2 py-2"
            type="button"
            onClick={removeImage}
          >
            <i className="bi bi-trash3"></i>
            Remove Photo
          </button>
        </li>
      </ul>
    </div>
  );

  // UI component to show error message
  const renderErrorMessage = () => (
    <div className="alert alert-danger d-flex align-items-center gap-2 py-2 px-3 mt-1 rounded-3"
      style={{ fontSize: "12.5px" }}>
      <i className="bi bi-exclamation-circle-fill"></i>
      {values.errormsg}
    </div>
  );

  // Loader + progress bar shown as an overlay centered on the avatar image
  const showLoader = () => (
    <div
      className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center rounded-circle"
      style={{ background: "rgba(255,255,255,0.70)", zIndex: 3 }}
    >
      <div
        className="spinner-border text-app mb-2"
        role="status"
        style={{ width: "32px", height: "32px", borderWidth: "3px" }}
      />
      {/* slim progress bar */}
      <div className="progress w-75" style={{ height: "4px" }}>
        <div
          className="progress-bar bg-app"
          role="progressbar"
          style={{ width: `${progress}%` }}
        />
      </div>
      <small className="text-app fw-semibold mt-1" style={{ fontSize: "10px" }}>
        {progress}%
      </small>
    </div>
  );

  return (
    <div className="d-flex flex-column align-items-center">
      {/* hidden image used to detect broken URLs */}
      <img
        src={url || DEFAULT_IMAGE_URL}
        className="d-none"
        alt=""
        onError={() => failure("Select valid image format")}
      />

      {/* Outer wrapper: relative so badge & loader can be absolutely positioned */}
      <div className="position-relative" style={{ width: "150px", height: "150px" }}>

        {/* Avatar ring */}
        <div className="rounded-circle border-app p-1 shadow-sm w-100 h-100">
          {url ? (
            <ModalImage
              small={url || DEFAULT_IMAGE_URL}
              large={url || DEFAULT_IMAGE_URL}
              hideZoom={true}
              hideDownload={true}
              className="rounded-circle profileImg img-fluid"
            />
          ) : (
            <img
              src={DEFAULT_IMAGE_URL}
              alt="Profile placeholder"
              className="img-fluid rounded-circle profileImg"
            />
          )}
        </div>

        {/* Loader centered over image / badge at bottom-right */}
        {loading ? showLoader() : !url ? renderCameraIcon() : renderEditOptions()}
      </div>

      {/* Hidden file input */}
      <input
        id="photo"
        type="file"
        accept="image/*"
        className="d-none"
        disabled={isDisabled}
        onChange={handleProfileSelect}
      />

      {/* Error message */}
      {values.errormsg && renderErrorMessage()}
    </div>
  );
};

export default ProfilePhoto;
